import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraDevice, ConfigResponse, CustomerJoinRequest } from '@fastgarage/protocol';
import { ApiError, getConfig, joinSession, startCustomerSession } from '../api';
import { SessionRuntime, type ConnectionUiState } from '../livekit';
import { acquireCamera, mediaErrorMessage, type CameraAcquisition } from '../media';
import type { UploadStatus } from '../uploadQueue';
import { CameraScreen } from './CameraScreen';
import { CodeWaitScreen } from './CodeWaitScreen';
import { EndScreen } from './EndScreen';
import { StartForm } from './StartForm';

/**
 * Single flow component used by both entry points:
 * '/'            → code path (customer starts, reads the code out on the phone)
 * '/s/:linkToken' → link path (staff started; skips the code screen entirely)
 *
 * Guardrail: before the form is submitted (Kennzeichen + consent) no
 * getUserMedia call happens and no camera/video element exists in the DOM —
 * the SessionRuntime is only created after a successful join/start API call.
 */

type Phase = 'formular' | 'warten' | 'kamera' | 'beendet';

/** Used only when GET /api/config fails — the server text (with the concrete
 * retention period interpolated) is the normal path. */
const FALLBACK_CONSENT_TEXT =
  'Ich willige ein, dass die Werkstatt Fastgarage während der Video-Schadenaufnahme ' +
  'Einzelfotos meines Fahrzeugs aufnimmt und zur Dokumentation und Bearbeitung meines ' +
  'Auftrags speichert. Livebild und Ton werden nur während des Termins an die ' +
  'Werkstatt übertragen; es erfolgt keine Video- oder Tonaufzeichnung. Die Fotos ' +
  'werden nach Ablauf der Aufbewahrungsfrist automatisch gelöscht. Ich kann diese ' +
  'Einwilligung jederzeit mit Wirkung für die Zukunft gegenüber der Werkstatt ' +
  'widerrufen.';

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  // Media failures get their own wording per cause — a blocked permission, an
  // in-app browser and an occupied camera need three different instructions.
  return mediaErrorMessage(err) ?? 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
}

export interface SessionFlowProps {
  linkToken?: string;
}

export function SessionFlow({ linkToken }: SessionFlowProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('formular');
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [kennzeichen, setKennzeichen] = useState('');
  const [code, setCode] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState<ConnectionUiState>('verbunden');
  const [staffConnected, setStaffConnected] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    transferred: 0,
    queued: 0,
    failed: 0,
  });
  const [pointer, setPointer] = useState<{ x: number; y: number; at: number } | null>(null);
  const [hint, setHint] = useState<{ text: string; at: number } | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null);

  const runtimeRef = useRef<SessionRuntime | null>(null);
  /** Camera opened in the tap but not yet owned by a runtime — unmounting in
   * that window would otherwise leave it running. */
  const pendingCameraRef = useRef<CameraAcquisition | null>(null);
  /** Codes and link tokens are one-time — keep issued credentials so a retry
   * (e.g. after a denied camera permission) does not re-consume them. */
  const credsRef = useRef<{
    sessionId: string;
    customerToken: string;
    joinCode: string | null;
    kennzeichen: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((cfg) => {
        if (!cancelled) setConfig(cfg);
      })
      .catch(() => undefined); // Fallback consent text is used instead.
    return () => {
      cancelled = true;
    };
  }, []);

  // Dispose the live session on unmount — including a camera that was opened
  // for a session which never came to be.
  useEffect(
    () => () => {
      runtimeRef.current?.dispose();
      runtimeRef.current = null;
      pendingCameraRef.current?.stream.getTracks().forEach((track) => track.stop());
      pendingCameraRef.current = null;
    },
    [],
  );

  const beginLive = useCallback(
    async (sessionId: string, customerToken: string, camera: CameraAcquisition) => {
      const runtime = new SessionRuntime(
        { sessionId, customerToken },
        {
          onLocalStream: (stream) => setLocalStream(stream),
          onCameras: (list, activeId) => {
            setCameras(list);
            setActiveCameraId(activeId);
          },
          onStaffPresence: (present) => {
            setStaffConnected(present);
            if (present) {
              setPhase((p) => (p === 'warten' ? 'kamera' : p));
            }
          },
          onConnection: (state) => setConnection(state),
          onDisconnected: () => {
            setPhase((p) => (p === 'warten' || p === 'kamera' ? 'beendet' : p));
          },
          onUploadStatus: (status) => setUploadStatus(status),
          onPointer: (x, y) => setPointer({ x, y, at: Date.now() }),
          onHint: (text) => setHint({ text, at: Date.now() }),
        },
      );
      runtimeRef.current = runtime;
      try {
        await runtime.start(camera);
      } catch (err) {
        runtime.dispose();
        runtimeRef.current = null;
        throw err;
      }
    },
    [],
  );

  /** joinCode === null → primary button; otherwise the 6-digit code path. */
  const handleSubmit = useCallback(
    async (joinCode: string | null) => {
      setBusy(true);
      setFormError(null);
      // The camera is opened FIRST, still inside the tap that submitted the
      // form — Kennzeichen and consent are validated by StartForm before
      // onSubmit fires, so the guardrail ("nothing before consent") holds.
      // Doing it after the join request would put getUserMedia behind a
      // network round trip; in-app browsers and older iOS then swallow the
      // request without ever showing a prompt.
      let camera: CameraAcquisition;
      try {
        camera = await acquireCamera({ facing: 'environment' });
        pendingCameraRef.current = camera;
      } catch (err) {
        setFormError(errorMessage(err));
        setBusy(false);
        return; // No session was created — the code/link stays unused.
      }
      try {
        const cached = credsRef.current;
        let creds: { sessionId: string; customerToken: string };
        if (cached && cached.joinCode === joinCode && cached.kennzeichen === kennzeichen) {
          creds = cached;
        } else if (linkToken || joinCode) {
          const body: CustomerJoinRequest = linkToken
            ? { kennzeichen, consent: true, linkToken }
            : { kennzeichen, consent: true, code: joinCode ?? undefined };
          const res = await joinSession(body);
          creds = { sessionId: res.sessionId, customerToken: res.customerToken };
          credsRef.current = { ...creds, joinCode, kennzeichen };
        } else {
          const res = await startCustomerSession({ kennzeichen, consent: true });
          setCode(res.code);
          creds = { sessionId: res.sessionId, customerToken: res.customerToken };
          credsRef.current = { ...creds, joinCode: null, kennzeichen };
        }
        // Link and code joins skip the code screen entirely.
        setPhase(linkToken || joinCode ? 'kamera' : 'warten');
        await beginLive(creds.sessionId, creds.customerToken, camera);
        pendingCameraRef.current = null; // The runtime owns it from here on.
      } catch (err) {
        // Never leave a camera running that no session owns any more.
        camera.stream.getTracks().forEach((track) => track.stop());
        pendingCameraRef.current = null;
        setFormError(errorMessage(err));
        setPhase('formular');
      } finally {
        setBusy(false);
      }
    },
    [beginLive, kennzeichen, linkToken],
  );

  const handleBeenden = useCallback(() => {
    if (window.confirm('Video-Termin wirklich beenden?')) {
      void runtimeRef.current?.end();
    }
  }, []);

  const handleVideoElement = useCallback((el: HTMLVideoElement | null) => {
    runtimeRef.current?.setPreviewVideo(el);
  }, []);

  const handleSelectCamera = useCallback((deviceId: string) => {
    if (!deviceId) return;
    void runtimeRef.current?.selectCameraById(deviceId);
  }, []);

  if (phase === 'formular') {
    return (
      <StartForm
        consentText={config?.consentText ?? FALLBACK_CONSENT_TEXT}
        androidApkUrl={config?.androidApkUrl ?? null}
        androidApkSizeBytes={config?.androidApkSizeBytes ?? null}
        impressumUrl={config?.impressumUrl ?? null}
        datenschutzUrl={config?.datenschutzUrl ?? null}
        kennzeichen={kennzeichen}
        onKennzeichenChange={setKennzeichen}
        busy={busy}
        error={formError}
        showCodeEntry={!linkToken}
        onSubmit={handleSubmit}
      />
    );
  }

  if (phase === 'beendet') {
    return (
      <EndScreen
        transferred={uploadStatus.transferred}
        queued={uploadStatus.queued}
        failed={uploadStatus.failed}
      />
    );
  }

  return (
    <div className="live-root">
      {phase === 'warten' && code ? (
        <CodeWaitScreen code={code} />
      ) : (
        <CameraScreen
          localStream={localStream}
          kennzeichen={kennzeichen}
          connection={connection}
          staffConnected={staffConnected}
          uploadStatus={uploadStatus}
          pointer={pointer}
          hint={hint}
          cameras={cameras}
          activeCameraId={activeCameraId}
          onSelectCamera={handleSelectCamera}
          onVideoElement={handleVideoElement}
          onBeenden={handleBeenden}
        />
      )}
    </div>
  );
}
